import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Unlock, Ban, CalendarClock, ArrowLeftRight, AlertTriangle, User, Car } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function detail(err, fb) {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fb;
}

export default function BookingOverrideDialog({ token, booking, vehicles, onClose, onDone }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [mode, setMode] = useState("menu"); // menu | release | cancel | edit | transfer
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [dates, setDates] = useState({ pickup_date: booking.start || booking.pickup_date || "", return_date: booking.end || booking.return_date || "" });
  const [newVehicle, setNewVehicle] = useState("");

  const bid = booking.booking_id;

  const call = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); toast.success(successMsg); onDone(); }
    catch (e) { toast.error(detail(e, "Action failed")); }
    finally { setBusy(false); }
  };

  const doRelease = () => call(() => axios.post(`${API}/bookings/${bid}/release-dates`, { reason }, auth), "Dates released — vehicle is available again.");
  const doCancel = () => call(() => axios.post(`${API}/bookings/${bid}/cancel`, { reason }, auth), "Booking cancelled.");
  const doTransfer = () => {
    if (!newVehicle) { toast.error("Choose a vehicle"); return; }
    call(() => axios.post(`${API}/bookings/${bid}/swap`, { new_vehicle: newVehicle, reason: reason || "Owner transfer" }, auth), "Booking transferred.");
  };
  const doEdit = async (force = false) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/bookings/${bid}/dates`, { pickup_date: dates.pickup_date, return_date: dates.return_date, force }, auth);
      toast.success("Booking dates updated."); onDone();
    } catch (e) {
      if (e?.response?.status === 409 && !force) {
        if (window.confirm(detail(e, "These dates overlap another reservation. Save anyway?"))) return doEdit(true);
      } else toast.error(detail(e, "Could not update dates"));
    } finally { setBusy(false); }
  };

  const field = "w-full px-3 py-2 rounded-md bg-black border border-white/15 text-white text-sm focus:border-[#F2B705] outline-none [color-scheme:dark]";
  const actionBtn = "w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#0C0C0C] border border-white/10 text-left hover:border-white/30 transition-colors";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-md" data-testid="booking-override-dialog">
        <DialogHeader>
          <DialogTitle>Booking — Owner Override</DialogTitle>
          <DialogDescription className="text-white/50">Manage this reservation on the calendar.</DialogDescription>
        </DialogHeader>

        {/* Booking summary */}
        <div className="rounded-lg bg-black/40 border border-white/10 p-3 space-y-1.5 text-sm">
          <div className="flex items-center gap-2"><User size={14} className="text-[#F2B705]" /> <span className="text-white font-semibold" data-testid="override-customer">{booking.label}</span></div>
          <div className="flex items-center gap-2 text-white/70"><Car size={14} className="text-white/40" /> {booking.vehicle || "—"}</div>
          <div className="flex items-center gap-2 text-white/70"><CalendarClock size={14} className="text-white/40" /> {booking.start} → {booking.end}</div>
          <div className="text-xs"><span className="px-2 py-0.5 rounded-full bg-white/10 text-white/70 uppercase">{booking.status}</span></div>
        </div>

        {mode === "menu" && (
          <div className="space-y-2" data-testid="override-menu">
            <button data-testid="override-release-btn" onClick={() => { setReason(""); setMode("release"); }} className={actionBtn}>
              <Unlock size={18} className="text-emerald-400 shrink-0" /><div><div className="font-semibold">Release Dates</div><div className="text-white/40 text-xs">Free the calendar, keep the booking record</div></div>
            </button>
            <button data-testid="override-edit-btn" onClick={() => setMode("edit")} className={actionBtn}>
              <CalendarClock size={18} className="text-sky-400 shrink-0" /><div><div className="font-semibold">Edit Booking Dates</div><div className="text-white/40 text-xs">Change pickup / return dates</div></div>
            </button>
            <button data-testid="override-transfer-btn" onClick={() => setMode("transfer")} className={actionBtn}>
              <ArrowLeftRight size={18} className="text-violet-400 shrink-0" /><div><div className="font-semibold">Transfer to Another Vehicle</div><div className="text-white/40 text-xs">Move this booking to a different car</div></div>
            </button>
            <button data-testid="override-cancel-btn" onClick={() => { setReason(""); setMode("cancel"); }} className={actionBtn}>
              <Ban size={18} className="text-red-400 shrink-0" /><div><div className="font-semibold">Cancel Booking</div><div className="text-white/40 text-xs">Cancel & notify the customer</div></div>
            </button>
          </div>
        )}

        {mode === "release" && (
          <div className="space-y-3" data-testid="override-release-panel">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-200 text-sm">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>This will make the vehicle available for new bookings. The customer's booking record will remain in the system. Continue?</span>
            </div>
            <input className={field} placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="override-release-reason" />
            <div className="flex gap-2 justify-end">
              <BackBtn onClick={() => setMode("menu")} />
              <button data-testid="override-release-confirm" onClick={doRelease} disabled={busy} className="px-4 py-2 rounded-md bg-emerald-500 text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Release Dates</button>
            </div>
          </div>
        )}

        {mode === "cancel" && (
          <div className="space-y-3" data-testid="override-cancel-panel">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-200 text-sm">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>This cancels the booking and notifies the customer. The record is kept but marked cancelled.</span>
            </div>
            <input className={field} placeholder="Cancellation reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="override-cancel-reason" />
            <div className="flex gap-2 justify-end">
              <BackBtn onClick={() => setMode("menu")} />
              <button data-testid="override-cancel-confirm" onClick={doCancel} disabled={busy} className="px-4 py-2 rounded-md bg-red-500 text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Cancel Booking</button>
            </div>
          </div>
        )}

        {mode === "edit" && (
          <div className="space-y-3" data-testid="override-edit-panel">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[11px] text-white/45 block mb-1">Pickup</label><input type="date" className={field} value={dates.pickup_date} onChange={(e) => setDates((s) => ({ ...s, pickup_date: e.target.value }))} data-testid="override-edit-pickup" /></div>
              <div><label className="text-[11px] text-white/45 block mb-1">Return</label><input type="date" className={field} value={dates.return_date} onChange={(e) => setDates((s) => ({ ...s, return_date: e.target.value }))} data-testid="override-edit-return" /></div>
            </div>
            <div className="flex gap-2 justify-end">
              <BackBtn onClick={() => setMode("menu")} />
              <button data-testid="override-edit-confirm" onClick={() => doEdit(false)} disabled={busy} className="px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-semibold disabled:opacity-60 flex items-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Save Dates</button>
            </div>
          </div>
        )}

        {mode === "transfer" && (
          <div className="space-y-3" data-testid="override-transfer-panel">
            <div><label className="text-[11px] text-white/45 block mb-1">Transfer to</label>
              <select className={field} value={newVehicle} onChange={(e) => setNewVehicle(e.target.value)} data-testid="override-transfer-select">
                <option value="">Select a vehicle…</option>
                {vehicles.filter((v) => `${v.make} ${v.model}`.toLowerCase() !== (booking.vehicle || "").toLowerCase())
                  .map((v) => <option key={v.id} value={`${v.make} ${v.model}`}>{v.year} {v.make} {v.model}</option>)}
              </select>
            </div>
            <input className={field} placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="flex gap-2 justify-end">
              <BackBtn onClick={() => setMode("menu")} />
              <button data-testid="override-transfer-confirm" onClick={doTransfer} disabled={busy} className="px-4 py-2 rounded-md bg-violet-500 text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Transfer</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BackBtn({ onClick }) {
  return <button onClick={onClick} className="px-4 py-2 rounded-md bg-[#0C0C0C] border border-white/10 text-white/70 text-sm font-semibold">Back</button>;
}
